import React, { createContext, useContext, useEffect, useRef, useState, useMemo } from 'react';

// Types
export type ScrollDirection = 'up' | 'down';

interface ScrollDirectionContextValue {
  direction: ScrollDirection;
  scrollY: number;
}

const ScrollDirectionContext = createContext<ScrollDirectionContextValue | undefined>(undefined);

export const ScrollDirectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [direction, setDirection] = useState<ScrollDirection>('down');
  const [scrollY, setScrollY] = useState<number>(typeof window !== 'undefined' ? window.scrollY : 0);
  const lastScrollY = useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY > lastScrollY.current) {
        setDirection('down');
      } else if (currentY < lastScrollY.current) {
        setDirection('up');
      }
      lastScrollY.current = currentY;
      setScrollY(currentY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Create memoized context value so provider only updates when inputs change
  const contextValue = useMemo<ScrollDirectionContextValue>(() => ({ direction, scrollY }), [direction, scrollY]);

  return (
    <ScrollDirectionContext.Provider value={contextValue}>
      {children}
    </ScrollDirectionContext.Provider>
  );
};

export function useScrollDirection(): ScrollDirection {
  const ctx = useContext(ScrollDirectionContext);
  if (!ctx) throw new Error('useScrollDirection must be used within a ScrollDirectionProvider');
  return ctx.direction;
}

/**
 * Hook to get the current vertical scroll position.
 */
export function useScrollPosition(): number {
  const ctx = useContext(ScrollDirectionContext);
  if (!ctx) throw new Error('useScrollPosition must be used within a ScrollDirectionProvider');
  return ctx.scrollY;
} 
